// AdminKpisBackofficePage — KPIs de aderência IA do backoffice
import { useEffect, useState } from 'react';
import { admin } from '../../lib/api.js';

const TIPO_LABEL = {
  reagendar_visita_para_outro_slot: 'Reagendar p/ outro slot',
  apenas_observacao: 'Apenas observação',
  atribuir_tecnico: 'Atribuir técnico',
  reordenar_rota: 'Reordenar rota',
  cancelar_visita: 'Cancelar visita',
};

function fmtSeg(s) {
  if (s == null) return '—';
  if (s < 60) return `${s}s`;
  if (s < 3600) return `${Math.round(s/60)}min`;
  return `${(s/3600).toFixed(1)}h`;
}
function pct(n, total) {
  if (!total) return 0;
  return Math.round((n / total) * 100);
}

export default function AdminKpisBackofficePage() {
  const [data, setData] = useState(null);
  const [dias, setDias] = useState(30);
  const [erro, setErro] = useState(null);

  useEffect(() => {
    admin.kpisBackoffice(dias).then(setData).catch(e => setErro(e.uiMessage));
  }, [dias]);

  if (erro) return <div className="card error">{erro}</div>;
  if (!data) return <div className="muted">Carregando KPIs...</div>;

  const g = data.globais;
  const tokensTotal = g.input_tokens + g.output_tokens;

  return (
    <div className="stack">
      <div className="row between" style={{ marginBottom: 12 }}>
        <h1 style={{ margin: 0 }}>KPIs Backoffice · Ações IA</h1>
        <select value={dias} onChange={e => setDias(+e.target.value)} style={{ padding: 6 }}>
          <option value={7}>Últimos 7 dias</option>
          <option value={30}>Últimos 30 dias</option>
          <option value={90}>Últimos 90 dias</option>
        </select>
      </div>

      <div className="kpi-cards">
        <KpiCard label="Total propostas" valor={g.total} sublabel={`em ${dias} dias`} />
        <KpiCard label="Taxa de resposta" valor={g.taxa_resposta_pct + '%'}
                 sublabel={`${g.total - g.expiradas} respondidas`}
                 cor={g.taxa_resposta_pct >= 70 ? 'verde' : g.taxa_resposta_pct >= 40 ? 'amarelo' : 'vermelho'} />
        <KpiCard label="Taxa de aceite" valor={g.taxa_aceite_pct + '%'}
                 sublabel={`${g.executadas} executadas de ${g.executadas + g.rejeitadas} decididas`}
                 cor="azul" />
        <KpiCard label="Tempo médio decisão" valor={fmtSeg(g.tempo_decisao_med_seg)}
                 sublabel="entre propor e decidir" />
        <KpiCard label="Custo IA (tokens)"
                 valor={tokensTotal > 1e6 ? (tokensTotal/1e6).toFixed(1)+'M' : (tokensTotal/1e3).toFixed(0)+'k'}
                 sublabel={`in: ${(g.input_tokens/1e3).toFixed(0)}k · out: ${(g.output_tokens/1e3).toFixed(0)}k`} />
      </div>

      {g.expiradas / Math.max(1, g.total) > 0.5 && (
        <div className="alert-block">
          <strong>⚠ Atenção:</strong> {g.expiradas} de {g.total} propostas ({pct(g.expiradas, g.total)}%) expiraram
          por não serem decididas a tempo. Considere {' '}
          aumentar o tempo de expiração das propostas OU treinar a equipe pra responder mais rápido.
        </div>
      )}

      {/* Ranking por operador */}
      <div className="card" style={{ padding: 20, marginTop: 8 }}>
        <h3 style={{ marginTop: 0 }}>Ranking · operadores</h3>
        {data.ranking.length === 0 && <p className="muted">Nenhum operador com atividade.</p>}
        <table className="admin-table">
          <thead>
            <tr>
              <th>Operador</th>
              <th>Atribuídas</th>
              <th>Executadas</th>
              <th>Rejeitadas</th>
              <th>Expiradas</th>
              <th>Taxa resposta</th>
              <th>Taxa aceite</th>
              <th>Tempo médio</th>
            </tr>
          </thead>
          <tbody>
            {data.ranking.map(op => (
              <tr key={op.operador_id}>
                <td><strong>{op.nome_completo}</strong> <span style={{color:'#9ca3af', fontSize:11}}>@{op.username}</span></td>
                <td>{op.total_atribuidas}</td>
                <td style={{color: '#166534'}}>{op.executadas}</td>
                <td style={{color: '#92400e'}}>{op.rejeitadas}</td>
                <td style={{color: '#991b1b'}}>{op.expiradas}</td>
                <td>{op.taxa_resposta_pct != null ? <BadgePct pct={op.taxa_resposta_pct}/> : '—'}</td>
                <td>{op.taxa_aceite_pct != null ? <BadgePct pct={op.taxa_aceite_pct}/> : '—'}</td>
                <td style={{fontSize:11, color:'#6b7280'}}>{fmtSeg(op.tempo_decisao_med_seg)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Por tipo */}
      <div className="card" style={{ padding: 20 }}>
        <h3 style={{ marginTop: 0 }}>Por tipo de ação</h3>
        <table className="admin-table">
          <thead>
            <tr>
              <th>Tipo</th><th>Total</th><th>Executadas</th><th>Rejeitadas</th><th>Expiradas</th><th>Aceite</th>
            </tr>
          </thead>
          <tbody>
            {data.por_tipo.map(t => {
              const decididas = t.executadas + t.rejeitadas;
              const aceite = decididas > 0 ? Math.round(t.executadas / decididas * 100) : null;
              return (
                <tr key={t.tipo}>
                  <td><strong>{TIPO_LABEL[t.tipo] || t.tipo}</strong></td>
                  <td>{t.total}</td>
                  <td style={{color: '#166534'}}>{t.executadas}</td>
                  <td style={{color: '#92400e'}}>{t.rejeitadas}</td>
                  <td style={{color: '#991b1b'}}>{t.expiradas} <span style={{color:'#6b7280', fontSize:11}}>({pct(t.expiradas, t.total)}%)</span></td>
                  <td>{aceite != null ? <BadgePct pct={aceite}/> : '—'}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Volume diário (sparkline simples) */}
      {data.volume_diario.length > 0 && (
        <div className="card" style={{ padding: 20 }}>
          <h3 style={{ marginTop: 0 }}>Volume diário</h3>
          <SparklineVolume serie={data.volume_diario} />
        </div>
      )}
    </div>
  );
}

function KpiCard({ label, valor, sublabel, cor }) {
  const cores = {
    verde: '#059669', amarelo: '#d97706', vermelho: '#dc2626', azul: '#1e40af',
  };
  return (
    <div className="kpi-card">
      <div className="kpi-label">{label}</div>
      <div className="kpi-value" style={{ color: cor ? cores[cor] : '#1f2937' }}>{valor}</div>
      {sublabel && <div className="kpi-sublabel">{sublabel}</div>}
    </div>
  );
}

function BadgePct({ pct }) {
  const c = pct >= 70 ? '#166534' : pct >= 40 ? '#92400e' : '#991b1b';
  const bg = pct >= 70 ? '#dcfce7' : pct >= 40 ? '#fef3c7' : '#fee2e2';
  return <span style={{
    background: bg, color: c, padding: '2px 8px', borderRadius: 10,
    fontSize: 11, fontWeight: 700,
  }}>{pct}%</span>;
}

function SparklineVolume({ serie }) {
  const max = Math.max(...serie.map(s => s.total), 1);
  return (
    <div style={{ display: 'flex', alignItems: 'flex-end', gap: 2, height: 80, padding: '0 4px' }}>
      {serie.map((s, i) => {
        const h = (s.total / max) * 100;
        const tip = `${s.dia}: ${s.total} (✓${s.executadas} ✗${s.rejeitadas} ⏰${s.expiradas})`;
        return (
          <div key={i}
            title={tip}
            style={{
              flex: 1,
              minHeight: 2,
              height: `${h}%`,
              background: 'linear-gradient(to top, #ef4444 0%, #ef4444 ' +
                `${s.expiradas/Math.max(1,s.total)*100}%, ` +
                '#22c55e ' + `${s.expiradas/Math.max(1,s.total)*100}%, ` +
                '#22c55e 100%)',
              borderRadius: '2px 2px 0 0',
              cursor: 'help',
            }}
          />
        );
      })}
    </div>
  );
}
